import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BnblOfferSection from './Components/Shared/BnblOfferSection'
import BnblSwitchPackageDurationStep from './Components/Steps/SwitchPackageDuration/BnblSwitchPackageDurationStep'
import BnblSwitchPackageDurationStepFooter from './Components/Steps/SwitchPackageDuration/BnblSwitchPackageDurationStepFooter'
import { BnblSteps, getModalTitle, getOnBack } from './utils'
import { BnblPackageDates } from './Components/Steps/Home/BnblPackageDates'
import BnblPackageDatesGift from './Components/Steps/Gift/BnblPackageDatesGift'
import { useAppDispatch } from 'hooks/reduxHooks'
import BnblPackagePricing from './Components/Shared/BnblPackagePricing'
import { Divider } from './Components/Shared/Divider'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { updateBnblGifting } from 'actions/BnblModalActions'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Group from 'components/utils/Group'
import BnblChangePackageStep from './Components/Steps/ChangePackage/BnblChangePackageStep'

interface MappedProps {
  bnblModalPackages: Array<App.BnblModal>
}

interface Props {
  closeBnblModal: () => void
  isOpen: boolean
  offer: App.HotelOffer
}

function BnblModal(props: Props & MappedProps) {
  const { isOpen, closeBnblModal, offer, bnblModalPackages } = props
  const [step, setStep] = useState<BnblSteps>(BnblSteps.HOME)
  const isGift = bnblModalPackages?.[0]?.isGift ?? false
  const packageView = bnblModalPackages?.[0]?.packageView
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const [selectedPackageUniqueKey, setSelectedPackageUniqueKey] = useState(packageView?.package?.uniqueKey)

  const intl = useIntl()
  const title = getModalTitle(step, intl)
  const dispatch = useAppDispatch()
  const onBack = getOnBack(step, setStep, () => setSelectedPackageUniqueKey(packageView?.package?.uniqueKey), isGift)

  const onToggleGifting = useCallback(() => {
    dispatch(updateBnblGifting(offer.id, !isGift))
  }, [dispatch, isGift, offer.id])

  useEffect(() => {
    if (isGift) {
      setStep(BnblSteps.GIFT)
    } else {
      setStep(BnblSteps.HOME)
    }
  }, [isGift])

  if (!packageView) return null

  return (
    <ModalBase
      height="full"
      tabletHeight="auto"
      size="XL"
      isOpen={isOpen}
      onClose={closeBnblModal}
    >
      <ModalHeader
        title={title}
        onBackButtonClick={onBack}
        onCloseButtonClick={closeBnblModal}
      />
      {step === BnblSteps.GIFT && (
        <>
          <ModalBody>
            <ModalContent>
              <Group direction="vertical" gap={16}>
                {offer.bookByDate && offer.travelToDate && <BnblPackageDatesGift
                  bookByDate={offer.bookByDate}
                  travelByDate={offer.travelToDate}
                />}
                <BnblOfferSection offer={offer} setStep={setStep} />
                <CheckboxInput onClick={onToggleGifting} checked={isGift} size="large">
                  {intl.formatMessage({
                    id: 'quyArVUozz54A-xPbCO6',
                    defaultMessage: 'Gift this package to someone else',
                    description: 'Checkbox label to toggle gifting a BNBL package in gift step',
                  })}
                </CheckboxInput>
              </Group>
            </ModalContent>
          </ModalBody>
          <ModalFooter>
            <BnblPackagePricing offer={offer} isGift={isGift} />
          </ModalFooter>
        </>
      )}
      {step === BnblSteps.HOME && (
        <>
          <ModalBody>
            <ModalContent>
              <Group direction="vertical" gap={16}>
                <BnblPackageDates bookByDate={offer.bookByDate!} travelByDate={offer.travelToDate!} />
                <BnblOfferSection offer={offer} setStep={setStep} />
                {!isMemberOnlyOffer && <>
                  <Divider />
                  <CheckboxInput onClick={onToggleGifting} checked={isGift} size="large">
                    {intl.formatMessage({
                      id: 'k3XsTbrvrZ-IsvGiUZzj',
                      defaultMessage: 'Gift this package to someone else',
                      description: 'Checkbox label to toggle gifting a BNBL package in home step',
                    })}
                  </CheckboxInput>
                </>}
              </Group>
            </ModalContent>
          </ModalBody>
          <ModalFooter>
            <BnblPackagePricing offer={offer} isGift={isGift} />
          </ModalFooter>
        </>
      )}
      {step === BnblSteps.CHANGE_DURATION && (
        <>
          <ModalBody>
            <ModalContent>
              <BnblSwitchPackageDurationStep
                offer={offer}
                setStep={setStep}
                selectedPackageUniqueKey={selectedPackageUniqueKey}
                setSelectedPackageUniqueKey={setSelectedPackageUniqueKey}
                isGift={isGift}
              />
            </ModalContent>
          </ModalBody>
          <BnblSwitchPackageDurationStepFooter
            offerId={offer.id}
            selectedPackageUniqueKey={selectedPackageUniqueKey}
            setStep={setStep}
            isGift={isGift}
          />
        </>
      )}
      {step === BnblSteps.CHANGE_PACKAGE && (
        <ModalBody>
          <BnblChangePackageStep
            offer={offer}
            isGift={isGift}
            setStep={setStep}
            packageView={packageView}
            selectedPackageUniqueKey={selectedPackageUniqueKey}
            setSelectedPackageUniqueKey={setSelectedPackageUniqueKey}
          />
        </ModalBody>
      )}
    </ModalBase>
  )
}

function mapStateToProps(state: App.State, ownProps: Props): MappedProps {
  return {
    bnblModalPackages: state.offer.bnblModal[ownProps.offer.id],
  }
}

export default connect(mapStateToProps)(BnblModal)
